<script>
    import IconButton from '../../IconButton/v1/IconButton.svelte';
    import { createEventDispatcher } from 'svelte';
    import { guidV4 } from '@itslearning/atlas/generate/guid';
    import { rules } from './rules';
    import { lowComplexity, mediumComplexity, strongComplexity } from './complexity';

    const dispatch = createEventDispatcher();

    /** @type {string} Defaults to a random string */
    export let id = guidV4();
    /** @type {('enter'|'create')} */
    export let mode = 'create';
    /** @type {('low'|'medium'|'strong'|undefined)} */
    export let complexity = undefined;
    /** @type {boolean} */
    export let hideValue = true;

    /**
     * @typedef PasswordFieldTerms
     * @type {object}
     * @property {string} [password]
     * @property {string} [passwordRules]
     * @property {string} [passwordHidden]
     * @property {string} [passwordShown]
     * @property {string} [passwordHide]
     * @property {string} [passwordShow]
     * @property {string} [notValid]
     * @property {function} [minNumCharacters]
     * @property {string} [noUserIdentity]
     * @property {string} [containsCharactersFrom]
     * @property {string} [uppercase]
     * @property {string} [lowercase]
     * @property {string} [numbers]
     * @property {string} [symbols]
     *
     */

    /** @type {PasswordFieldTerms} */
    export let i18n = {};
    /** @type {object} */
    export let userInfo = { username: '', firstName: '', lastName: '' };
    /** @type {boolean} */
    export let hasError = false;
    /** @type {string} */
    export let errorMessage = '';
    /** @type {object} */
    export let minComplexityValue = { "low": 7, "medium": 10, "strong": 13 };
    /** @type {string} */
    export let describedBy = '';
    /** @type {string} */
    export let value = '';
    /** @type {'current-password'|'new-password'|'off'} */
    export let autocomplete = 'off';
    /** @type {boolean} */
    export let disabled = false;

    let isValid = true;

    /** @type {HTMLInputElement} */
    let inputElement;

    $: {
        if (!value) {
            isValid = true;
            if (inputElement) {
                inputElement.value = '';
            }
        }
    }

    $: passwordRules = mode === 'enter'
        ? undefined
        : rules(i18n, userInfo, minComplexityValue, complexity, value);

    $: createMode = mode === 'create';

    $: hideShowButton =
        'prom-password-button ' +
        (hideValue
            ? 'prom-icon-button__password-show'
            : 'prom-icon-button__password-hide');
    $: buttonAltText = hideValue ? i18n.passwordShow : i18n.passwordHide;

    let passwordReqTexts, passwordRequirements, errorList;

    $: {
        passwordReqTexts = '';

        if (mode === "create") {
            if (complexity === 'low') {
                passwordRequirements = lowComplexity(passwordRules);
            } else if (complexity === 'medium') {
                passwordRequirements = mediumComplexity(passwordRules);
            } else if (complexity === 'strong') {
                passwordRequirements = strongComplexity(passwordRules);
            }

            for (let key in passwordRequirements) {
                if (key === 'characterTypeRules') {
                    passwordReqTexts += i18n.containsCharactersFrom + ' ';
                }
                if (Array.isArray(passwordRequirements[key])) {
                    for (let i = 0; i < passwordRequirements[key].length; i++) {
                        passwordReqTexts += passwordRequirements[key][i].text + '. ';
                    }
                }
            }
        }
    }

    function validPassword() {
        errorList = [];

        const validationResults = Object.values(passwordRequirements).map((ruleGroup) => {
            return ruleGroup.map((rule) => {
                if (!rule.validation) {
                    errorList.push(rule.text);
                }
                return rule.validation;
            });
        }).flat();

        isValid = validationResults.every((result) => result);
        dispatch('isvalid', { isValid });
    }

    function inputValueHandler(event) {
        value = event.target.value;
    }

    function inputHandler() {
        return createMode ? isValid = true : null;
    }

    function blurHandler() {
        return createMode ? validPassword() : null;
    }

    let prevUserInfo = { ...userInfo };
    let prevMinComplexityValue = { ...minComplexityValue };

    $: {
        const userInfoChanged = JSON.stringify(userInfo) !== JSON.stringify(prevUserInfo);
        const minComplexityValueChanged = JSON.stringify(minComplexityValue) !== JSON.stringify(prevMinComplexityValue);

        if ((userInfoChanged || minComplexityValueChanged) && value.length) {
            validPassword();
        }

        prevUserInfo = { ...userInfo };
        prevMinComplexityValue = { ...minComplexityValue };
    }

    $: hasErrorClass = hasError || !isValid && value.length ? 'prom-password-field__has-error' : '';
    $: hasErrorId = hasError || !isValid && value.length ? `error-message-${id}` : '';

    $: {
        if (createMode && !complexity) {
            console.warn(
                'Password "complexity" must be set if in "create"-mode'
            );
        }
    }
</script>

<div class="prom-input-field">
    {#if createMode && passwordRequirements}
        <p id="password-hints-{id}" class="prom-password-rules">
            {i18n.passwordRules}
            <span class="screen-reader">
                {passwordReqTexts}
            </span>
        </p>
        <ul aria-hidden="true" class="prom-password-req-list">
            {#each Object.keys(passwordRequirements) as category}
                {#if category === 'primaryRules'}
                    {#each passwordRequirements[category] as { text }}
                        <li class="prom-password-req-item">
                            {text}
                        </li>
                    {/each}
                {:else if passwordRequirements[category].length > 0}
                    <li class="prom-password-req-item">
                        {#if category === 'characterTypeRules'}
                            {i18n.containsCharactersFrom}
                        {:else if category !== 'primaryRules'}
                            {category}
                        {/if}
                    </li>
                    <li>
                        <ul class="prom-password-req-list">
                            {#each passwordRequirements[category] as { text }}
                                <li class="prom-password-req-item">
                                    {text}
                                </li>
                            {/each}
                        </ul>
                    </li>
                {/if}
            {/each}
        </ul>
    {/if}

    <label for="password-{id}" class="prom-input-field__label">{i18n.password}</label>

    <span aria-live="polite" class="screen-reader"
        >{hideValue ? i18n.passwordHidden : i18n.passwordShown}</span>

    <div class="prom-password-field {hasErrorClass}">
        <div class="prom-password-field__container">
            <input bind:this={inputElement}
                class="prom-input-field__input"
                id="password-{id}"
                aria-required="true"
                type={hideValue ? "password" : "text"}
                {disabled}
                aria-describedby="{hasErrorId} {describedBy} password-hints-{id}"
                autocomplete={createMode ? 'new-password' : autocomplete}
                on:input={(event) => inputValueHandler(event)}
                on:input={inputHandler}
                on:blur={blurHandler}
                on:blur
                on:change />
            {#if !disabled} 
                <IconButton
                    htmlType="button"
                    iconClass={hideShowButton}
                    ariaLabel={buttonAltText}
                    on:click={() => (hideValue = !hideValue)} />
            {/if}
        </div>
    </div>

    <div 
        aria-live="assertive" 
        aria-atomic="true" 
        class="prom-error-message prom-error-message--shown" 
        id={hasErrorId ? hasErrorId : null}>
        {#if !isValid && value.length || hasError}
            {#if errorMessage}
                <p>{errorMessage}</p>
            {:else}
                {i18n.notValid}
                <span class="screen-reader">
                    {errorList.toString()}
                </span>

                <ul aria-hidden="true" class="prom-error-message prom-password-req-list">
                    {#each errorList as text}
                        <li class="prom-password-req-item">{text}</li>
                    {/each}
                </ul>
            {/if}
        {/if}
    </div>
</div>
